import "../../css/GlobalStyles.css";
import { Colors } from "../../utils/colors";
import { PlanTypeV4, SubscriptionPlanType } from "@markit/common.types";
import {
  CSSProperties,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import SubscriptionPlanCard from "./SubscriptionPlanCard";
import RectangleButton from "../Buttons/RectangleButton";
import { DropdownMenu, DropdownMenuItem } from "../DropdownMenu";
import {
  hasSubscription,
  MARKIT_HOTLINE_NUMBER,
  mostRecentSubscription,
  mostRecentSubscriptionPlan,
} from "@markit/common.utils";
import { useSelector } from "react-redux";
import {
  getAccountState,
  getIncompleteOnboardingFlows,
} from "../../redux/slices/accountSlice";
import { useLocation } from "react-router-dom";
import { useTheme } from "../../hooks/useTheme";
import { Icon } from "@iconify/react";
import {
  SUBSCRIPTION_PLAN_CARD_MAX_WIDTH,
  SUBSCRIPTION_PLAN_CARD_MIN_WIDTH,
  SUBSCRIPTION_PLANS_GAP,
} from "../../utils/constants/globalConstants";
import { checkIsDesktop } from "../../utils/screenResponsivenessUtils";
import { NavigationId } from "../../navigation/AppParamList";
import { useWindowResize } from "../../hooks/useWindowResize";
import { MixpanelContext } from "../../context/AnalyticsService";
import { testStripe } from "../../utils/stripeAccountUtils";
import DropdownArrowIcon from "../DropdownsAndTabs/DropdownArrowIcon";

const PLANS_LAYOUT_SNAP_POINTS = [
  { perRow: 4, gap: SUBSCRIPTION_PLANS_GAP },
  { perRow: 2, gap: SUBSCRIPTION_PLANS_GAP },
  { perRow: 1, gap: 0 },
] as const;

/** Plans per row from container width and 306px min card width (4 → 2x2 → 1-column). */
const getSubscriptionPlansPerRow = (containerWidth: number) => {
  for (const { perRow, gap } of PLANS_LAYOUT_SNAP_POINTS) {
    const requiredWidth =
      perRow * SUBSCRIPTION_PLAN_CARD_MIN_WIDTH + (perRow - 1) * gap;
    if (containerWidth >= requiredWidth) {
      return perRow;
    }
  }
  return 1;
};

type SubscriptionPlansProps = {
  selectedCardOption: number;
  setSelectedCardOption: (selectedCardOption: number) => void;
  itemOnPress: (cardOption: number) => void;
  homePage?: boolean;
  /** Horizontal padding on the parent wrapper (per side), for width estimates before measure. */
  contentPaddingInline?: number;
};

const SubscriptionPlans = (props: SubscriptionPlansProps) => {
  const {
    itemOnPress,
    selectedCardOption,
    setSelectedCardOption,
    homePage,
    contentPaddingInline = 14,
  } = props;
  const { theme } = useTheme();
  const mixpanel = useContext(MixpanelContext);
  const { accountData } = useSelector(getAccountState).account;
  const incompleteOnboardingFlows = useSelector(getIncompleteOnboardingFlows);
  const { changePlan } = useLocation().state || {};
  const pathname = useLocation().pathname;
  const windowWidth = useWindowResize();
  const plansContainerRef = useRef<HTMLDivElement>(null);
  const [plansContainerWidth, setPlansContainerWidth] = useState(0);
  const [isPlanDropdownOpen, setIsPlanDropdownOpen] = useState(false);

  const isDesktopSize = useMemo(
    () => checkIsDesktop(windowWidth, true),
    [windowWidth]
  );

  const availablePlansWidth = useMemo(() => {
    if (plansContainerWidth > 0) {
      return plansContainerWidth;
    }
    return Math.max(0, windowWidth - contentPaddingInline * 2);
  }, [plansContainerWidth, windowWidth, contentPaddingInline]);

  const plansPerRow = useMemo(
    () => getSubscriptionPlansPerRow(availablePlansWidth),
    [availablePlansWidth]
  );

  const squeezedCardWidth = useMemo(() => {
    const gapTotal = (plansPerRow - 1) * SUBSCRIPTION_PLANS_GAP;
    const rawWidth = (availablePlansWidth - gapTotal) / plansPerRow;
    return Math.min(
      SUBSCRIPTION_PLAN_CARD_MAX_WIDTH,
      Math.max(SUBSCRIPTION_PLAN_CARD_MIN_WIDTH, rawWidth)
    );
  }, [availablePlansWidth, plansPerRow]);

  useEffect(() => {
    const element = plansContainerRef.current;
    if (!element) return;

    const updateWidth = () => {
      setPlansContainerWidth(element.clientWidth);
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);
    return () => observer.disconnect();
  }, [plansPerRow]);

  const isOnboarding = useMemo(
    () => pathname === NavigationId.ONBOARDING,
    [pathname]
  );

  // Onboarding only - flow types the user selected on Customize Experience,
  // used to highlight the matching feature line on the plan that unlocks it.
  const highlightedFlowTypes = useMemo(
    () =>
      isOnboarding
        ? incompleteOnboardingFlows.map((flow) => flow.id)
        : undefined,
    [isOnboarding, incompleteOnboardingFlows]
  );

  const subscriptionPlanItems = [
    PlanTypeV4.FREE,
    PlanTypeV4.ESSENTIAL,
    PlanTypeV4.PRO,
    PlanTypeV4.BUSINESS,
  ];

  const mostRecentSubPlan = useMemo(
    () =>
      mostRecentSubscriptionPlan(accountData, testStripe(accountData.uid)) ||
      (!isOnboarding && !homePage ? PlanTypeV4.FREE : undefined),
    [accountData, homePage, isOnboarding]
  );

  const mostRecentPriceAmount = useMemo(() => {
    const subscription = mostRecentSubscription(accountData);
    if (subscription) {
      return subscription.price[0].amount;
    }
    return 0;
  }, [accountData]);

  const getInTouchOnPress = useCallback(() => {
    if (mixpanel && homePage) {
      mixpanel.track("Website: Pricing Pressed Get In Touch", {
        distinct_id: accountData.uid,
      });
    }
  }, [accountData.uid, homePage, mixpanel]);

  const onOpenChange = useCallback((open: boolean) => {
    setIsPlanDropdownOpen(open);
  }, []);

  const renderButtonTitle = useCallback(
    (plan: SubscriptionPlanType) => {
      if (changePlan) {
        if (mostRecentPriceAmount > plan.price) {
          return "Downgrade to " + plan.name;
        } else if (mostRecentPriceAmount < plan.price) {
          return "Upgrade to " + plan.name;
        } else {
          return "Current Plan";
        }
      } else if (
        !homePage &&
        hasSubscription(accountData) &&
        plan === mostRecentSubPlan
      ) {
        return "Current Plan";
      } else {
        return "Continue with " + (plan.price === 0 ? "Free" : plan.name);
      }
    },
    [
      accountData,
      changePlan,
      homePage,
      mostRecentPriceAmount,
      mostRecentSubPlan,
    ]
  );

  const buttonColor = useCallback(
    (isCurrentPlan: boolean, index: number) =>
      homePage
        ? selectedCardOption === index || isCurrentPlan
          ? Colors.WHITE
          : Colors.TRANSPARENT
        : selectedCardOption === index || isCurrentPlan || !isDesktopSize
          ? Colors.BLACK
          : Colors.TRANSPARENT,
    [homePage, isDesktopSize, selectedCardOption]
  );

  const buttonTextColor = useCallback(
    (isCurrentPlan: boolean, index: number) =>
      homePage
        ? selectedCardOption === index || isCurrentPlan
          ? Colors.BLACK
          : Colors.WHITE
        : selectedCardOption === index || isCurrentPlan
          ? Colors.WHITE
          : Colors.BLACK,
    [homePage, selectedCardOption]
  );

  const buttonBorderColor = useCallback(
    (isCurrentPlan: boolean) =>
      isCurrentPlan
        ? `1px solid ${theme.DividerColor.borderColor}`
        : `1px solid ${homePage ? Colors.WHITE : Colors.BLACK}`,
    [homePage, theme.DividerColor.borderColor]
  );

  const renderPlanCard = useCallback(
    (subscriptionPlan: SubscriptionPlanType, index: number) => {
      const isCurrentPlan =
        !homePage &&
        hasSubscription(accountData) &&
        mostRecentSubPlan.name === subscriptionPlan.name;

      return (
        <SubscriptionPlanCard
          cardButton={
            <RectangleButton
              buttonLabel={
                <span style={{ fontWeight: 400 }}>
                  {renderButtonTitle(subscriptionPlan)}
                </span>
              }
              onPress={() => itemOnPress(index)}
              altColor={buttonColor(isCurrentPlan, index)}
              altTextColor={buttonTextColor(isCurrentPlan, index)}
              disabled={isCurrentPlan}
              containerStyles={{
                padding: plansPerRow === 4 ? 14 : undefined,
                border: buttonBorderColor(isCurrentPlan),
              }}
              altPaddingHorz={plansPerRow === 4 ? undefined : 14}
              altPaddingVert={plansPerRow === 4 ? undefined : 14}
            />
          }
          subscriptionPlan={subscriptionPlan}
          isSelected={selectedCardOption === index}
          selectOnPress={
            isCurrentPlan ? undefined : () => setSelectedCardOption(index)
          }
          homePage={homePage}
          highlightedFlowTypes={highlightedFlowTypes}
        />
      );
    },
    [
      accountData,
      buttonBorderColor,
      buttonColor,
      buttonTextColor,
      highlightedFlowTypes,
      homePage,
      itemOnPress,
      mostRecentSubPlan,
      plansPerRow,
      renderButtonTitle,
      selectedCardOption,
      setSelectedCardOption,
    ]
  );

  const styles = {
    squeezedPlanCard: {
      width: squeezedCardWidth,
      minWidth: SUBSCRIPTION_PLAN_CARD_MIN_WIDTH,
      maxWidth: SUBSCRIPTION_PLAN_CARD_MAX_WIDTH,
      flex: "0 0 auto",
      boxSizing: "border-box",
    } as CSSProperties,
  };

  const singleColumnCardWidth = useMemo(
    () =>
      Math.min(
        SUBSCRIPTION_PLAN_CARD_MAX_WIDTH,
        Math.max(SUBSCRIPTION_PLAN_CARD_MIN_WIDTH, availablePlansWidth)
      ),
    [availablePlansWidth]
  );

  const planDropdownItems: DropdownMenuItem[] = useMemo(
    () =>
      subscriptionPlanItems.map((plan, index) => ({
        title: plan.name,
        key: plan.name,
        onPress: () => setSelectedCardOption(index),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setSelectedCardOption]
  );

  const planSelectorRow = (
    <div
      className="AlignedRowSpaced"
      style={{ width: "100%", alignItems: "center" }}
    >
      <span
        style={{
          fontSize: 28,
          fontWeight: 500,
          color: homePage ? Colors.WHITE : Colors.BLACK,
        }}
      >
        Plans
      </span>
      <DropdownMenu
        dropdownItems={planDropdownItems}
        trigger={["click"]}
        placement="bottomRight"
        altWidth={150}
        dark={homePage}
        onOpenChange={onOpenChange}
      >
        <div
          className="AlignedRow"
          style={{
            gap: 6,
            paddingInline: 14,
            paddingBlock: 10,
            borderRadius: 12,
            backgroundColor: homePage ? Colors.GRAY7 : Colors.GRAY6,
            cursor: "pointer",
          }}
        >
          <span
            className="bodyMedium"
            style={{ color: homePage ? Colors.WHITE : Colors.BLACK }}
          >
            {subscriptionPlanItems[selectedCardOption].name}
          </span>
          <DropdownArrowIcon
            isOpen={isPlanDropdownOpen}
            color={Colors.WHITE}
            height={14}
          />
        </div>
      </DropdownMenu>
    </div>
  );

  const plansLayout =
    plansPerRow === 4 ? (
      <div
        className="AlignedRow"
        style={{
          gap: SUBSCRIPTION_PLANS_GAP,
          alignItems: "flex-start",
          justifyContent: "center",
          width: "100%",
          minWidth: 0,
        }}
      >
        {subscriptionPlanItems.map((subscriptionPlan, index) => (
          <div
            className="slide-up"
            style={styles.squeezedPlanCard}
            key={subscriptionPlan.name}
          >
            {renderPlanCard(subscriptionPlan, index)}
          </div>
        ))}
      </div>
    ) : plansPerRow === 2 ? (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(2, ${squeezedCardWidth}px)`,
          justifyContent: "center",
          gap: SUBSCRIPTION_PLANS_GAP,
          width: "100%",
          minWidth: 0,
        }}
      >
        {subscriptionPlanItems.map((subscriptionPlan, index) => (
          <div
            className="slide-up"
            key={subscriptionPlan.name}
            style={styles.squeezedPlanCard}
          >
            {renderPlanCard(subscriptionPlan, index)}
          </div>
        ))}
      </div>
    ) : (
      <div
        className="ColumnCentering"
        style={{ gap: 20, width: "100%", minWidth: 0 }}
      >
        {homePage ? (
          <div style={{ width: singleColumnCardWidth }}>{planSelectorRow}</div>
        ) : null}
        <div
          key={selectedCardOption}
          className="fade-in"
          style={{
            ...styles.squeezedPlanCard,
            width: singleColumnCardWidth,
            flex: undefined,
          }}
        >
          {renderPlanCard(
            subscriptionPlanItems[selectedCardOption],
            selectedCardOption
          )}
        </div>
      </div>
    );

  return (
    <div className="ColumnNormal" style={{ width: "100%", minWidth: 0 }}>
      <div
        ref={plansContainerRef}
        style={{
          width: "100%",
          minWidth: 0,
          maxWidth: "100%",
        }}
      >
        {plansLayout}
      </div>
      <div className="ColumnCentering" style={{ paddingTop: 24 }}>
        <span
          className="bodyRegular"
          style={{
            color: homePage ? Colors.WHITE : Colors.GRAY1,
            textAlign: "center",
            width: plansPerRow === 4 ? "100%" : "70%",
          }}
        >
          For customized enterprise solutions{" "}
          <a
            href={`sms:${MARKIT_HOTLINE_NUMBER}`}
            onClick={getInTouchOnPress}
            style={{ color: homePage ? Colors.BLUE5 : Colors.BLACK }}
          >
            Get in touch
          </a>
          <Icon
            icon="ion:chevron-right"
            height={10}
            style={{
              color: homePage ? Colors.BLUE5 : Colors.BLACK,
              marginBottom: -1,
              marginLeft: 1,
            }}
          />
        </span>
      </div>
    </div>
  );
};

export default SubscriptionPlans;
